/**
 * Task operations on page documents (ProseMirror JSON). Shared by the editor
 * (checking a box) and the task views (write-back into the source page), so both
 * follow the same Obsidian Tasks rules: done date, cancelled date, recurrence.
 */
import type { JSONContent } from '@tiptap/core';
import {
  addDays,
  dueToken,
  fromIsoDate,
  nextOccurrence,
  parseTaskText,
  PRIORITY_EMOJI,
  removeToken,
  STATUS_CHAR,
  statusFromChar,
  uuidv7,
  type TaskPriority,
  type TaskStatus,
} from '@nexus/core';

const diffDays = (a: string, b: string) =>
  Math.round((fromIsoDate(b).getTime() - fromIsoDate(a).getTime()) / 86_400_000);

/** Readable text of inline content, as the task parser expects it. */
export function inlineText(nodes: JSONContent[] | undefined): string {
  return (nodes ?? [])
    .map((n) => {
      if (n.type === 'text') return n.text ?? '';
      if (n.type === 'wikiLink')
        return (n.attrs?.alias as string | null) ?? (n.attrs?.target as string) ?? '';
      if (n.type === 'hashtag') return `#${n.attrs?.tag as string}`;
      if (n.type === 'hardBreak') return ' ';
      if (n.type === 'rawInline') return (n.attrs?.text as string) ?? '';
      return inlineText(n.content);
    })
    .join('');
}

/** The paragraph holding a task's text (first child of the task item). */
const firstParagraph = (item: JSONContent) =>
  item.content?.[0]?.type === 'paragraph' ? item.content[0] : null;

export function taskItemText(item: JSONContent): string {
  return inlineText(firstParagraph(item)?.content);
}

/** Give every task item a unique id (new, pasted or duplicated items). Returns whether anything changed. */
export function ensureTaskIds(doc: JSONContent): { doc: JSONContent; changed: boolean } {
  const seen = new Set<string>();
  let changed = false;
  const walk = (n: JSONContent): JSONContent => {
    let node = n;
    if (n.type === 'taskItem') {
      const id = n.attrs?.taskId as string | null | undefined;
      if (!id || seen.has(id)) {
        node = { ...n, attrs: { ...n.attrs, taskId: uuidv7() } };
        changed = true;
      }
      seen.add(node.attrs!.taskId as string);
    }
    return node.content ? { ...node, content: node.content.map(walk) } : node;
  };
  const out = walk(doc);
  return { doc: changed ? out : doc, changed };
}

/**
 * Undo a bug (before 0.9.0): pressing Enter after a done task copied its status
 * `x` into the new, unchecked item. Such items (unchecked, status `x`, no `✅`
 * date) are open again. Returns null when nothing changed.
 */
export function fixSplitDoneStatus(doc: JSONContent): JSONContent | null {
  let changed = false;
  const walk = (n: JSONContent): JSONContent => {
    let node = n;
    if (
      n.type === 'taskItem' &&
      n.attrs?.checked !== true &&
      (n.attrs?.status === 'x' || n.attrs?.status === 'X') &&
      !taskItemText(n).includes('✅')
    ) {
      node = { ...n, attrs: { ...n.attrs, status: ' ' } };
      changed = true;
    }
    return node.content ? { ...node, content: node.content.map(walk) } : node;
  };
  const out = walk(doc);
  return changed ? out : null;
}

/**
 * Edit the trailing plain-text run of a task's first paragraph, where the Tasks
 * tokens live. Links, tags and formatted text before it stay untouched.
 */
function editText(
  item: JSONContent,
  edit: (text: string, afterOtherContent: boolean) => string,
): JSONContent {
  const para = firstParagraph(item);
  if (!para) return item;
  const nodes = para.content ?? [];
  let start = nodes.length;
  while (start > 0 && nodes[start - 1]!.type === 'text' && !nodes[start - 1]!.marks?.length)
    start--;
  const edited = edit(
    nodes
      .slice(start)
      .map((n) => n.text ?? '')
      .join(''),
    start > 0,
  );
  const content = [...nodes.slice(0, start), ...(edited ? [{ type: 'text', text: edited }] : [])];
  return { ...item, content: [{ ...para, content }, ...(item.content ?? []).slice(1)] };
}

export function setItemToken(item: JSONContent, emoji: string, value: string | null): JSONContent {
  return editText(item, (text, afterOtherContent) => {
    const base = removeToken(text, emoji).replace(/\s+$/, '');
    if (value === null) return base;
    const sep = base || afterOtherContent ? ' ' : '';
    return `${base}${sep}${emoji}${value ? ` ${value}` : ''}`;
  });
}

export function setItemPriority(item: JSONContent, priority: TaskPriority | null): JSONContent {
  let out = item;
  for (const emoji of Object.values(PRIORITY_EMOJI)) out = setItemToken(out, emoji, null);
  return priority ? setItemToken(out, PRIORITY_EMOJI[priority], '') : out;
}

/**
 * Change a task's status. Done adds `✅ date`, cancelled adds `❌ date`, reopening
 * removes them. Completing a recurring task first inserts the next occurrence,
 * like the Tasks plugin. Returns the item(s) to put in place of the original.
 */
export function applyTaskStatus(
  item: JSONContent,
  status: TaskStatus,
  today: string,
): JSONContent[] {
  let updated: JSONContent = {
    ...item,
    attrs: { ...item.attrs, status: STATUS_CHAR[status], checked: status === 'done' },
  };
  updated = setItemToken(updated, '✅', status === 'done' ? today : null);
  updated = setItemToken(updated, '❌', status === 'cancelled' ? today : null);

  const wasDone =
    statusFromChar((item.attrs?.status as string) ?? ' ') === 'done' ||
    item.attrs?.checked === true;
  if (status !== 'done' || wasDone) return [updated];
  const parsed = parseTaskText(taskItemText(item));
  const dates = { due: parsed.due, scheduled: parsed.scheduled, start: parsed.start };
  const base = dates.due ?? dates.scheduled ?? dates.start;
  if (!parsed.recurrence || !base) return [updated];
  const whenDone = /when done|als klaar/i.test(parsed.recurrence);
  const nextBase = nextOccurrence(parsed.recurrence, whenDone ? today : base);
  if (!nextBase) return [updated];
  // Every date moves by the same number of days (keeps "scheduled 2 days before due").
  const shift = diffDays(base, nextBase);
  let next: JSONContent = {
    ...item,
    attrs: { ...item.attrs, status: ' ', checked: false, taskId: uuidv7() },
    content: item.content?.slice(0, 1),
  };
  next = setItemToken(next, '✅', null);
  if (dates.due)
    next = setItemToken(next, '📅', dueToken(addDays(dates.due, shift), parsed.dueTime));
  if (dates.scheduled) next = setItemToken(next, '⏳', addDays(dates.scheduled, shift));
  if (dates.start) next = setItemToken(next, '🛫', addDays(dates.start, shift));
  return [next, updated];
}

/** Replace the task item with `taskId` by what `fn` returns. Returns null if not found. */
export function updateTaskItem(
  doc: JSONContent,
  taskId: string,
  fn: (item: JSONContent) => JSONContent[],
): JSONContent | null {
  let found = false;
  const walk = (n: JSONContent): JSONContent => {
    if (!n.content || found) return n;
    const content: JSONContent[] = [];
    for (const child of n.content) {
      if (!found && child.type === 'taskItem' && child.attrs?.taskId === taskId) {
        found = true;
        content.push(...fn(child));
      } else {
        content.push(found ? child : walk(child));
      }
    }
    return { ...n, content };
  };
  const out = walk(doc);
  return found ? out : null;
}

/**
 * The task item with `taskId`, and the blocks nested under its text (subtasks, notes).
 */
export function findTaskItem(doc: JSONContent, taskId: string): JSONContent | null {
  let found: JSONContent | null = null;
  const walk = (n: JSONContent) => {
    if (found) return;
    if (n.type === 'taskItem' && n.attrs?.taskId === taskId) found = n;
    else n.content?.forEach(walk);
  };
  walk(doc);
  return found;
}

/**
 * Replace a task item by a bullet with `inline` (a link to the task page it became).
 * Its task list is split around it, since a task list holds only task items.
 */
export function replaceTaskItemByBullet(
  doc: JSONContent,
  taskId: string,
  inline: JSONContent[],
): JSONContent | null {
  let found = false;
  const walk = (n: JSONContent): JSONContent => {
    if (!n.content || found) return n;
    const content: JSONContent[] = [];
    for (const child of n.content) {
      const i =
        child.type === 'taskList'
          ? (child.content ?? []).findIndex((c) => c.attrs?.taskId === taskId)
          : -1;
      if (found || i < 0) {
        content.push(found ? child : walk(child));
        continue;
      }
      found = true;
      const items = child.content!;
      const list = (part: JSONContent[]) => (part.length ? [{ ...child, content: part }] : []);
      content.push(
        ...list(items.slice(0, i)),
        {
          type: 'bulletList',
          attrs: { tight: true, bullet: '-' },
          content: [{ type: 'listItem', content: [{ type: 'paragraph', content: inline }] }],
        },
        ...list(items.slice(i + 1)),
      );
    }
    return { ...n, content };
  };
  const out = walk(doc);
  return found ? out : null;
}

/** Append a task at the end of a page (to its last task list, or a new one). */
export function appendTask(doc: JSONContent, text: string): { doc: JSONContent; taskId: string } {
  const taskId = uuidv7();
  const item: JSONContent = {
    type: 'taskItem',
    attrs: { checked: false, status: ' ', taskId },
    content: [{ type: 'paragraph', content: text ? [{ type: 'text', text }] : [] }],
  };
  const blocks = [...(doc.content ?? [])];
  // Drop a trailing empty paragraph (new page) so the task list comes first.
  while (
    blocks.length &&
    blocks[blocks.length - 1]!.type === 'paragraph' &&
    !blocks[blocks.length - 1]!.content?.length
  ) {
    blocks.pop();
  }
  const last = blocks[blocks.length - 1];
  if (last?.type === 'taskList') {
    blocks[blocks.length - 1] = { ...last, content: [...(last.content ?? []), item] };
  } else {
    blocks.push({ type: 'taskList', attrs: { tight: true, bullet: '-' }, content: [item] });
  }
  return { doc: { ...doc, content: blocks }, taskId };
}

export interface ExtractedTask {
  id: string;
  parentId: string | null;
  position: number;
  text: string;
  statusChar: string;
  linkIds: string[];
  /** Link targets without a page id yet (resolved by name when indexing). */
  linkTargets: string[];
}

/** All task items of a document, in order (only those with an id). */
export function extractTasks(doc: JSONContent): ExtractedTask[] {
  const out: ExtractedTask[] = [];
  const walk = (n: JSONContent, parentId: string | null) => {
    let parent = parentId;
    if (n.type === 'taskItem') {
      const id = n.attrs?.taskId as string | null;
      if (id) {
        const para = firstParagraph(n);
        const links = (para?.content ?? []).filter((c) => c.type === 'wikiLink');
        const linkIds = links.filter((c) => c.attrs?.pageId).map((c) => c.attrs!.pageId as string);
        const linkTargets = links
          .filter((c) => !c.attrs?.pageId)
          .map((c) => c.attrs!.target as string);
        out.push({
          id,
          parentId,
          position: out.length,
          text: taskItemText(n),
          statusChar:
            n.attrs?.checked === true && n.attrs?.status === ' '
              ? 'x'
              : ((n.attrs?.status as string) ?? ' '),
          linkIds,
          linkTargets,
        });
        parent = id;
      }
    }
    n.content?.forEach((c) => walk(c, parent));
  };
  walk(doc, null);
  return out;
}
